import React, { useEffect, useMemo, useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import investorApi from '/src/apis/investorApi'
import BarWithLineChart from '/src/components/Reusable/Charts/BarWithLineChart'
import { formatAmount } from '/src/lib/helpers'

const toChartData = (dataset, width) => ({
    color_table: ["#b5002a", "#FF8811"],
    dataset,
    id: "deployment-timeline",
    line_data: dataset,
    tooltip_data: dataset.map((d) => [d[0], d[2], formatAmount(d[1])]),
    tooltip_keys: ["Year", "Deals", "Cumulative Capital"],
    height: 240,
    width,
    show_yAxis: true,
    value_param: "",
    margin_top: 30,
    margin_bottom: 40,
    cornered: true,
    smooth_line: true,
    line_width: 2,
    dot_radius: 5,
    show_grids: true,
    grid_from_right_axis: true,
    show_left_axis: true,
    show_label_on_bars: false,
    show_label_on_line: false,
    n_axis_intervals: 3,
    x_padding: 0.15,
    max_bar_width: 60,
});

const NO_FILTERS = []

export default function DeploymentTimeline({ entityId, entityType, fundFilters = NO_FILTERS }) {
    const [dataset, setDataset] = useState(null)
    const [width,   setWidth]   = useState(null)
    const wrapRef  = useRef(null)
    // Discard responses from superseded requests (e.g. rapid fund tab switching)
    const fetchId  = useRef(0)

    useEffect(() => {
        const el = wrapRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        let timer = null
        const ro = new ResizeObserver(() => {
            clearTimeout(timer)
            timer = setTimeout(() => {
                const w = el.offsetWidth
                if (w > 100) setWidth(w)
            }, 150)
        })
        ro.observe(el)
        return () => { ro.disconnect(); clearTimeout(timer) }
    }, [])

    useEffect(() => {
        const requestId = ++fetchId.current
        setDataset(null)

        investorApi.getICEDeploymentTimeline(entityId, entityType, fundFilters).then(rows => {
            if (requestId !== fetchId.current) return
            if (!rows || rows.length === 0) { setDataset([]); return }

            const firstActive = rows.findIndex(r => r.number_of_investments > 0)
            if (firstActive === -1) { setDataset([]); return }

            const built = rows.slice(firstActive).map(r => [
                String(r.year),
                r.estimated_deployed_capital,
                r.number_of_investments,
            ])
            setDataset(built)
        }).catch(() => {
            if (requestId !== fetchId.current) return
            setDataset([])
        })
    }, [entityId, entityType, fundFilters])

    const chartData = useMemo(
        () => dataset && dataset.length > 0 && width ? toChartData(dataset, width) : null,
        [dataset, width]
    )

    return (
        <div className="pi-card">
            <div className="pi-card__title">Deployment timeline</div>
            <div className="pi-card__subtitle">Annual deals + cumulative capital deployed</div>

            <div ref={wrapRef} className="pi-chart-wrap">
                {dataset === null || !width ? (
                    <Skeleton height={240} borderRadius={6} />
                ) : dataset.length === 0 ? (
                    <div className="pi-empty pi-empty--no-data" style={{ height: 240 }}>No data available.</div>
                ) : (
                    <BarWithLineChart data={chartData} />
                )}
            </div>
        </div>
    )
}
